import React, { useEffect, useState } from "react";
import { Form, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import SlidePanel from "../../../components/SlidePanel/SlidePanel";
import * as opexService from "../../../services/opexService";
import { getVendorCurrency } from "../../../utils/currency";

const MAX_RECEIPT_BYTES = 5 * 1024 * 1024;

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const PAYMENT_METHODS = [
  { value: "CASH", label: "Cash" },
  { value: "BANK_TRANSFER", label: "Bank Transfer" },
  { value: "CHEQUE", label: "Cheque" },
  { value: "CARD", label: "Card" },
  { value: "ONLINE", label: "Online" },
];

// Records an actual cost-paid entry against an Operating Expense item -
// this is what "record the payment with date" ends up in history as.
// Recurring items auto-advance next_due_date server-side once this posts.
function RecordPaymentPanel({ show, onHide, item, onSaved }) {
  const [amountPaid, setAmountPaid] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState("BANK_TRANSFER");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const currency = getVendorCurrency();

  useEffect(() => {
    if (item) {
      setAmountPaid(item.amount);
      setPaymentDate(new Date().toISOString().slice(0, 10));
      setPaymentMethod("BANK_TRANSFER");
      setReferenceNumber("");
      setNotes("");
      setReceiptFile(null);
    }
  }, [item, show]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_RECEIPT_BYTES) {
      toast.error("Receipt must be under 5MB");
      e.target.value = "";
      return;
    }
    setReceiptFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amountPaid || Number(amountPaid) <= 0) return toast.error("Enter an amount greater than 0");
    if (!paymentDate) return toast.error("Select the payment date");

    setSubmitting(true);
    try {
      const receipt = receiptFile ? await readFileAsDataUrl(receiptFile) : undefined;
      const res = await opexService.recordPayment(item.id, {
        amount_paid: Number(amountPaid),
        payment_date: paymentDate,
        payment_method: paymentMethod,
        reference_number: referenceNumber || undefined,
        notes: notes || undefined,
        receipt,
      });
      if (res?.success === false) {
        toast.error(res.message || "Could not record payment");
      } else {
        toast.success("Payment recorded");
        onSaved();
      }
    } catch (err) {
      toast.error(err.message || "Could not record payment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SlidePanel show={show} onHide={onHide} title={`Record Payment — ${item?.title || ""}`} width={460}>
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Amount paid ({currency})</Form.Label>
          <Form.Control type="number" step="0.01" min="0" value={amountPaid} onChange={(e) => setAmountPaid(e.target.value)} required />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Date paid</Form.Label>
          <Form.Control type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} required />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Payment method</Form.Label>
          <Form.Select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
            {PAYMENT_METHODS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Reference / transaction number (optional)</Form.Label>
          <Form.Control value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Receipt / invoice (optional)</Form.Label>
          <Form.Control type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={handleFileChange} />
          <Form.Text className="text-muted">JPG, PNG, WEBP or PDF, up to 5MB.</Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Notes (optional)</Form.Label>
          <Form.Control as="textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Form.Group>

        <div className="d-flex justify-content-end gap-2 mt-4 pt-3" style={{ borderTop: "1px solid #e5e7eb" }}>
          <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting} style={{ background: "var(--button-color)", border: "none", color: "#fff" }}>
            {submitting ? "Saving…" : "Record Payment"}
          </Button>
        </div>
      </Form>
    </SlidePanel>
  );
}

export default RecordPaymentPanel;
